import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useEffect } from 'react'

import { SUITE_USER_PROFILE_QUERY_KEYS } from '../constants/query-keys'
import type { ProfileDto } from '../dtos/user-profile.dto'
import { fetchUserProfile } from '../services/user-profile.service'
import { logError, logInfo } from '../utils/sso-logger'

/**
 * Same contract as adamo-id `useUserProfile` (retries, cache). Disabled when legacy Pay login is active.
 */
export function useSuiteUserProfile(enabled = true): UseQueryResult<ProfileDto, Error> {
  const query = useQuery({
    queryKey: SUITE_USER_PROFILE_QUERY_KEYS.keys.profile(),
    queryFn: async () => {
      logInfo('useSuiteUserProfile — queryFn executing')
      const response = await fetchUserProfile()
      return response.data
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })

  useEffect(() => {
    logInfo('useSuiteUserProfile — state', {
      enabled,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isFetched: query.isFetched,
      isError: query.isError,
      isSuccess: query.isSuccess,
      fetchStatus: query.fetchStatus,
      failureCount: query.failureCount
    })
  }, [enabled, query.isLoading, query.isFetching, query.isFetched, query.isError, query.isSuccess, query.fetchStatus, query.failureCount])

  useEffect(() => {
    if (query.data) {
      logInfo('useSuiteUserProfile — profile loaded', {
        uuid: query.data.uuid,
        email: query.data.email,
        allowedProducts: query.data.allowedProducts
      })
    }
  }, [query.data])

  useEffect(() => {
    if (query.error) {
      logError('useSuiteUserProfile — profile error (will retry or redirect)', query.error, {
        failureCount: query.failureCount
      })
    }
  }, [query.error, query.failureCount])

  return query
}
